import { AuthContext, AuthStorage, BatchUploadWidget, CsvExportWidget, LoadingLine, ToastProvider, PatientDetailsRedirect } from '@enter/core';
import { InactivityDetector, ProviderMessagingProvider } from 'components';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import React, { useContext, useEffect, useRef } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { RestrictedRoute } from 'router';
import { DialogProvider } from 'services';
import { IInjectedRootStore, RootStore } from 'stores';
import { ChargemasterDetail, ChargemasterListing } from './chargemasters';
import { ByFilter, ClaimDetail, ClaimEditor, ClaimListing } from './claims';
import { Documents } from './documents';
import { Eligibility } from './eligibility';
import { Fax } from './fax';
import { GuarantorDetails, Guarantors } from './guarantors';
import { Mail } from './mail';
import { MainSidebar } from './main.sidebar';
import * as S from './main.styles';
import { MainTopnav } from './main.topnav';
import { MessagingOutlet } from './messaging';
import { MessagingLegacyOutlet } from './messaging/messaging-legacy-outlet';
import { StatementDetails } from './patients';
import { Payments } from './payments';
import { Payers } from './payers';
import { PayersProviders } from './payers-providers/payers-providers';
import { Overpayments } from './overpayments';
import { PatientCollections, InvoiceStatement } from './patient-collections';
import { Reports } from './reports';
import { Configuration } from './configuration';
import { CodingRules } from './settings/coding-rules';
import { Settings } from './settings';
import { Settlements } from './settlements';
import { Transactions } from './transactions';
import { Phone } from './phone';

type IMainProps = IInjectedRootStore;

const PatientDetailsRedirectWrapper: React.FC<{ rootStore: RootStore }> = ({ rootStore }) => {
    const { accountId } = useParams<{ accountId: string }>();
    return (
        <PatientDetailsRedirect
            isSupport={false}
            accountId={accountId}
            // Demo only. The real app keeps its own lookup, which works there because nothing
            // refuses the request (NS-14).
            resolvePatientId={
                rootStore.isDemo()
                    ? (externalId: string) => rootStore.patientStore.resolvePatientIdByExternalId(externalId)
                    : undefined
            }
        />
    );
};

export const Main: React.FunctionComponent<IMainProps> = inject('rootStore')(
    observer((props: IMainProps): React.ReactElement => {
        const { rootStore } = props;
        const params = useParams();
        const location = useLocation();
        const context = useContext(AuthContext);
        const authStorage = new AuthStorage(context.clientId || '');

        // Extract accountId from URL params
        const accountId = params.accountId;
        const grantedAccountId = rootStore.account?.grantedAccountId;
        const isUuid = (id: string | undefined): boolean => !!id && id.length === 36;

        const { pathname, search } = location;
        const pathId = _.first(_.compact(pathname.split('/')));
        const isUnrecognizedPrefix = !isUuid(pathId) && isUuid(accountId);
        const hasAccessToPathAccount = rootStore.account?.accountAccess?.some(acc => acc.accountId === pathId);
        const isGrantDoesNotMatchPath =
            isUuid(pathId) && isUuid(grantedAccountId) && pathId !== grantedAccountId && hasAccessToPathAccount;
        const isInvalidAccountId =
            isUuid(pathId) && isUuid(grantedAccountId) && pathId !== grantedAccountId && !hasAccessToPathAccount;
        const isLegacyUrl = (!accountId || !isUuid(accountId)) && grantedAccountId && rootStore.account;

        const lastSwitchAttemptRef = useRef<string | null>(null);

        // Handle redirects and account switching
        useEffect(() => {
            (async () => {
                // If there is no accountId in the URL or it's not a valid UUID, treat it as
                // a legacy route and prefix it with the granted account. This keeps old URLs
                // like "/claims" or "/patients/:id/statement" working by turning them into
                // "/{grantedAccountId}/claims" or "/{grantedAccountId}/patients/:id/statement".
                if (isLegacyUrl) {
                    const defaultPath = '/claims';
                    const suffix = pathname && pathname !== '/' ? pathname : defaultPath;
                    window.location.href = `/${grantedAccountId}${suffix}${search}`;
                } else if (isInvalidAccountId) {
                    // Redirect to grantedAccountId if user doesn't have access to the pathId account
                    // This prevents infinite loops when trying to switch to invalid accounts
                    const pathWithoutAccountId = pathname.replace(`/${pathId}`, '');
                    const suffix = pathWithoutAccountId || '/claims';
                    window.location.href = `/${grantedAccountId}${suffix}${search}`;
                } else if (isUnrecognizedPrefix) {
                    // Redirect when non-UUID prefixes detected IF accountId is known
                    const defaultPath = '/claims';
                    const suffix = pathname || defaultPath;
                    window.location.href = `/${accountId}${suffix}${search}`;
                } else if (isGrantDoesNotMatchPath && context.switchAccount) {
                    // Switch accounts if grantedAccountId does not match current UUID prefix
                    // Only switch if storage accountId doesn't already match pathId to prevent infinite loop
                    if (authStorage.accountId !== pathId && lastSwitchAttemptRef.current !== pathId) {
                        lastSwitchAttemptRef.current = pathId as string;
                        try {
                            await context.switchAccount(pathId as string, pathname);
                        } catch (err) {
                            console.error('switchAccount failed', err);
                        }
                    }
                }
            })();
        }, [
            accountId,
            grantedAccountId,
            rootStore.account,
            context,
            isGrantDoesNotMatchPath,
            isInvalidAccountId,
            isUnrecognizedPrefix,
            isLegacyUrl,
            pathId,
            pathname,
            search,
            authStorage,
        ]);

        const renderNotFound = (): React.ReactElement => <Navigate to="/404" replace />;

        if (isLegacyUrl || isInvalidAccountId || isUnrecognizedPrefix || isGrantDoesNotMatchPath) {
            return <LoadingLine timeout={15000} />;
        }

        return (
            <ToastProvider>
                <DialogProvider>
                    <ProviderMessagingProvider>
                        <MainSidebar rootStore={rootStore} />
                        <MainTopnav rootStore={rootStore} />
                        <S.Main sidebarOpened={!rootStore.layoutStore.sidebarCollapsed}>
                            <Routes>
                                <Route path="/" element={<Navigate to="claims" replace />} />
                                <Route path="claims" element={<ClaimListing rootStore={rootStore} />} />
                                <Route path="claims/create" element={<ClaimEditor />} />
                                <Route path="claims/:claimId/edit" element={<ClaimEditor />} />
                                <Route path="claims/:claimId" element={<ClaimDetail rootStore={rootStore} />} />
                                <Route path="claims/by-:filterType/:id" element={<ByFilter rootStore={rootStore} />} />
                                <Route path="messaging/*" element={<MessagingOutlet rootStore={rootStore} />} />
                                <Route path="eligibility" element={<Eligibility />} />
                                <Route path="reports" element={<Reports rootStore={rootStore} />} />
                                <Route path="settings/*" element={<Settings />} />
                                <Route path="configuration" element={<Configuration />} />
                                <Route path="coders" element={<CodingRules />} />
                                {/* Hidden until vetted (FE-116) — see the note in `main.sidebar.tsx`.
                                    Removing the route as well as the link means a bookmarked or
                                    shared URL cannot reach it either. */}
                                <Route path="chargemasters" element={<ChargemasterListing />} />
                                <Route path="chargemasters/:chargemasterId" element={<ChargemasterDetail />} />
                                <Route path="payments" element={<Payments />} />
                                {/* Routing — where a payer's data goes. Distinct from
                            `payers-providers`, which is payer↔provider enrolment. */}
                        <Route path="payers" element={<Payers rootStore={rootStore} />} />
                        <Route path="payers-providers" element={<PayersProviders rootStore={rootStore} />} />
                                <Route path="overpayments" element={<Navigate to="overpayments/pending" replace />} />
                                <Route path="overpayments/:tab" element={<Overpayments />} />
                                <Route
                                    path="patient-collections"
                                    element={<Navigate to="patient-collections/needs" replace />}
                                />
                                <Route
                                    path="patient-collections/invoice/:collectionInvoiceId"
                                    element={<InvoiceStatement />}
                                />
                                <Route path="patient-collections/:tab" element={<PatientCollections />} />
                                <Route path="patients/:patientId/statement" element={<StatementDetails />} />
                                <Route
                                    path="patients/details/:externalId"
                                    element={<PatientDetailsRedirectWrapper rootStore={rootStore} />}
                                />
                                <Route path="documents" element={<Documents />} />
                                <Route path="guarantors" element={<Guarantors />} />
                                <Route path="guarantors/:id" element={<GuarantorDetails />} />
                                <Route path="transactions" element={<Transactions />} />
                                <Route path="mail" element={<Navigate to="mail/inbox" replace />} />
                                <Route path="mail/:tab" element={<Mail />} />
                                <Route path="phone" element={<Phone />} />

                                <Route path="fax" element={<Navigate to="fax/inbox" replace />} />
                                <Route path="fax/:tab" element={<Fax />} />

                                {/* <Route path="mail" element={<RestrictedRoute requires="DEV"><Mail /></RestrictedRoute>} /> */}
                                <Route
                                    path="settlements"
                                    element={
                                        <RestrictedRoute requires="ADMIN">
                                            <Settlements />
                                        </RestrictedRoute>
                                    }
                                />
                                <Route path="*" element={renderNotFound()} />
                            </Routes>
                        </S.Main>
                        {!rootStore.isLocal() && <InactivityDetector />}
                        <BatchUploadWidget batchUploadStore={rootStore.batchUploadStore} />
                        <CsvExportWidget />
                    </ProviderMessagingProvider>
                </DialogProvider>
            </ToastProvider>
        );
    }),
);
